// src/user/ParentChildRelationships.js
import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { API } from "../config/apiConfig";
import {
  Paper,
  Typography,
  Button,
  Stack,
  Snackbar,
  Alert,
  Box,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";

export default function ParentChildRelationships() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Request relationship state
  const [childEmailOrUsername, setChildEmailOrUsername] = useState("");
  const [requesting, setRequesting] = useState(false);
  
  // Pending requests state (for child)
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Relationships state (for parent)
  const [relationships, setRelationships] = useState([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  
  // Withdrawal state
  const [selectedChild, setSelectedChild] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [defaultPaymentAccount, setDefaultPaymentAccount] = useState(null);
  
  // Withdrawal history state
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const userId = decoded?.userId;

  // Fetch pending requests (for child)
  const fetchPendingRequests = async () => {
    if (!token) return;
    
    setLoadingRequests(true);
    try {
      const response = await axios.get(API.getParentChildRequests, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPendingRequests(response.data.requests || []);
      }
    } catch (err) {
      console.error("Error fetching pending requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Fetch relationships (for parent)
  const fetchRelationships = async () => {
    if (!token) return;
    
    setLoadingRelationships(true);
    try {
      const response = await axios.get(API.getParentChildRelationships, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setRelationships(response.data.relationships || []);
      }
    } catch (err) {
      console.error("Error fetching relationships:", err);
    } finally {
      setLoadingRelationships(false);
    }
  };

  // Fetch payment accounts and auto-select default PayPal account
  const fetchPaymentAccounts = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(API.savedPaymentAccounts, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const accounts = Array.isArray(response.data?.accounts) ? response.data.accounts : [];
      
      // Find default PayPal account (prefer IsDefault, then first PayPal)
      const defaultPayPal = accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal' && acc.IsDefault === true
      ) || accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
      );
      
      setDefaultPaymentAccount(defaultPayPal || null);
    } catch (err) {
      console.error("Error fetching payment accounts:", err);
    }
  };

  // Fetch withdrawal history
  const fetchWithdrawalHistory = async () => {
    if (!token) return;
    
    setLoadingHistory(true);
    try {
      const response = await axios.get(API.getParentChildWithdrawalHistory, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setWithdrawalHistory(response.data.withdrawals || []);
      }
    } catch (err) {
      console.error("Error fetching withdrawal history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPendingRequests();
      fetchRelationships();
      fetchPaymentAccounts();
      fetchWithdrawalHistory();
    }
  }, [token]);

  // Request relationship
  const handleRequestRelationship = async () => {
    if (!childEmailOrUsername.trim()) {
      setError("Please enter child's email or username");
      return;
    }

    setRequesting(true);
    setError("");
    
    try {
      const response = await axios.post(
        API.requestParentChildRelationship,
        { childEmailOrUsername: childEmailOrUsername.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess("Relationship request sent successfully");
        setChildEmailOrUsername("");
        fetchRelationships(); // Refresh relationships
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send relationship request");
    } finally {
      setRequesting(false);
    }
  };

  // Approve/Reject request
  const handleUpdateRequestStatus = async (relationshipId, status) => {
    try {
      const response = await axios.patch(
        API.updateParentChildRelationshipStatus(relationshipId),
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`Relationship request ${status.toLowerCase()} successfully`);
        fetchPendingRequests();
        fetchRelationships();
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${status.toLowerCase()} request`);
    }
  };

  // Open withdrawal dialog
  const handleOpenWithdrawDialog = (child) => {
    setSelectedChild(child);
    setWithdrawAmount("");
    setWithdrawDialogOpen(true);
    
    // Show error if no PayPal account found
    if (!defaultPaymentAccount) {
      setError("Please add a PayPal account in Payment Settings first");
      setTimeout(() => setWithdrawDialogOpen(false), 2000);
    }
  };

  // Handle withdrawal
  const handleWithdraw = async () => {
    if (!selectedChild) return;
    
    const amount = parseFloat(withdrawAmount);
    const availableBalance = parseFloat(selectedChild?.ChildBalance || 0);
    
    if (!withdrawAmount || isNaN(amount) || amount <= 0) {
      setError("Please enter a valid withdrawal amount");
      return;
    }
    
    if (amount > availableBalance) {
      setError(`Insufficient balance. Available: $${availableBalance.toFixed(2)}, Requested: $${amount.toFixed(2)}`);
      return;
    }
    
    if (!defaultPaymentAccount) {
      setError("No PayPal account found. Please add one in Payment Settings");
      return;
    }

    setWithdrawing(true);
    setError("");

    try {
      const response = await axios.post(
        API.createParentChildWithdrawal,
        {
          childUserId: selectedChild.ChildUserID,
          amount: amount,
          currencyCode: "USD",
          paymentAccountId: defaultPaymentAccount.AccountID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess("Withdrawal request submitted successfully");
        setWithdrawDialogOpen(false);
        setWithdrawAmount("");
        setSelectedChild(null);
        fetchRelationships();
        fetchWithdrawalHistory();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create withdrawal request");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Family Relationships
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage parent-child relationships and withdraw from children's balances
      </Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Request Relationship" />
        <Tab label="Pending Requests" />
        <Tab label="My Children" />
        <Tab label="Withdrawal History" />
      </Tabs>

      {/* Request Relationship Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Request Parent-Child Relationship
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the child's email or username to send a relationship request
          </Typography>
          
          <Stack spacing={2} sx={{ maxWidth: 500 }}>
            <TextField
              label="Child Email or Username"
              value={childEmailOrUsername}
              onChange={(e) => setChildEmailOrUsername(e.target.value)}
              fullWidth
              disabled={requesting}
            />
            <Button
              variant="contained"
              onClick={handleRequestRelationship}
              disabled={requesting || !childEmailOrUsername.trim()}
              startIcon={requesting ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {requesting ? "Sending Request..." : "Send Request"}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Pending Requests Tab (for child) */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pending Relationship Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Approve or reject relationship requests from parents
          </Typography>

          {loadingRequests ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : pendingRequests.length === 0 ? (
            <Alert severity="info">No pending requests</Alert>
          ) : (
            <Stack spacing={2}>
              {pendingRequests.map((request) => (
                <Card key={request.RelationshipID}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle1">
                          {request.ParentFullName || request.ParentUserName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {request.ParentEmail}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Requested: {new Date(request.RequestedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleUpdateRequestStatus(request.RelationshipID, "Approved")}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => handleUpdateRequestStatus(request.RelationshipID, "Rejected")}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {/* My Children Tab (for parent) */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            My Children
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            View your children and withdraw from their balances
          </Typography>

          {loadingRelationships ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : relationships.length === 0 ? (
            <Alert severity="info">No approved relationships</Alert>
          ) : (
            <Grid container spacing={2}>
              {relationships.map((relationship) => (
                <Grid item xs={12} md={6} key={relationship.RelationshipID}>
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle1">
                            {relationship.ChildFullName || relationship.ChildUserName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {relationship.ChildEmail}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Balance
                          </Typography>
                          <Typography variant="h6" color="primary">
                            ${parseFloat(relationship.ChildBalance || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<AccountBalanceWalletIcon />}
                          onClick={() => handleOpenWithdrawDialog(relationship)}
                          disabled={parseFloat(relationship.ChildBalance || 0) <= 0}
                          fullWidth
                        >
                          Withdraw
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Withdrawal History Tab */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Withdrawal History
          </Typography>

          {loadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : withdrawalHistory.length === 0 ? (
            <Alert severity="info">No withdrawal history</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Child</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {withdrawalHistory.map((withdrawal) => (
                    <TableRow key={withdrawal.WithdrawalRequestID}>
                      <TableCell>
                        {new Date(withdrawal.RequestedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {withdrawal.ChildFullName || withdrawal.ChildUserName}
                      </TableCell>
                      <TableCell>
                        ${parseFloat(withdrawal.Amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={withdrawal.Status}
                          color={
                            withdrawal.Status === "Paid"
                              ? "success"
                              : withdrawal.Status === "Pending"
                              ? "warning"
                              : withdrawal.Status === "Rejected"
                              ? "error"
                              : "default"
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdraw from {selectedChild?.ChildFullName || selectedChild?.ChildUserName}'s Balance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Available Balance
              </Typography>
              <Typography variant="h6" color="primary">
                ${parseFloat(selectedChild?.ChildBalance || 0).toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label="Withdrawal Amount"
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              fullWidth
              inputProps={{ min: 0, max: selectedChild?.ChildBalance || 0, step: 0.01 }}
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Account
              </Typography>
              {defaultPaymentAccount ? (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="body1">
                    {defaultPaymentAccount.AccountName || "PayPal Account"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {defaultPaymentAccount.AccountNumber || defaultPaymentAccount.Email || "PayPal"}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  No PayPal account found. Please add one in Payment Settings.
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)} disabled={withdrawing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWithdraw}
            disabled={withdrawing || !withdrawAmount || !defaultPaymentAccount}
          >
            {withdrawing ? "Processing..." : "Withdraw"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

